import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import ApexCharts from 'apexcharts'
import * as d3 from "d3";
import * as htmlToImage from "html-to-image";
import { Tooltip } from "bootstrap";
import Resizable from "resizable";


export default class extends Controller {
    static targets = [
        "calCurvePanel",
        "calCurveGraph",
        "calCurvePointsList",

        "chromatogramPanel",
        "sampleName",
        "analyteGraphContainer",
        "istdGraphContainer",
        "fiaMinRt",
        "fiaMaxRt",

        "subStatus",
        "resultsTable",
        "form",
        "formatGroup",

        "peakPickingModeBtn",
        "deconvolutionModeBtn",
        "peakPickingOptions",
        "autoIntegrationAnalyteBtn",
        "autoIntegrationIstdBtn",

        "analyteRefWindow",
        "istdRefWindow"
    ]

    connect() {
        this.HAS_NO_ISTDS = {
            "123.1/121.9": "4-Methylcatechol",
            "253.2/131.9": "Daidzein",
            "297.1/253": "enterolactone",
            "349.2/269.1": "Estrone sulfate",
            "269.1/132.9": "Genistein",
            "356.1/282": "Monobutyl-Phthalate",
            "271.1/151": "Naringenin",
        }
        this.STATUS_TIMEOUT = 5000
        this.ACTIVE_STATES = ["Preprocessing", "Profiling"]
        this.UPDATE_ACTIONS = [  // actions that notify submission has been updated in the backend (directly from worker)
            "apply_new_rt",
            "profile_exp_samples",
            "cal_point_removal",
        ]
        this.QUEUED_ACTIONS = [
            "queued_apply_new_rt",
            "queued_profile_exp_samples",
            "queued_cal_point_removal",
        ]

        this.recalibrateAllAnalytes = false
        this.DEFAULT_CAL_CURVE_MARKER_SIZE = 10
        this.CAL_CURVE_MARKER_COLOR = "#4596B6"
        this.CAL_CURVE_MARKER_COLOR_ALT = "#ffe083"
        this.CAL_CURVE_MARKER_COLOR_UNFILLED = "#ffffff"
        this.CAL_CURVE_MARKER_COLOR_SELECTED = "#99c799"
        this.CAL_CURVE_PLOT = null
        this.CAL_CURVE_DATA = null

        this.reintegrateAllSamples = false
        this.autoAdjust_analyte = true
        this.autoAdjust_istd = true

        // Integration mode - default to peak_picking
        this.integrationMode = "peak_picking"
        this.currentViewMode = "peak_picking"
        this.hasDeconvolution = this.element.dataset.hasDeconvolution === "true"

        this.imageDownloadFormat = this.formatGroupTarget.querySelector('input[name="chromDownloadFormat"]:checked')?.value || "png";
        this.OG_HEIGHT = 500
        this.HEIGHT_MULTIPLIER = 1 / 3
        this.MULTI_SAMPLE = false
        this.MULTI_SAMPLE_LIST = []  // contains the first element {analyte/istd: chart, data} as the main sample at the top, and the rest as the multi-sample chromatograms with sample id only
        this.MULTI_SAMPLE_TOGGLE = $("#multi-sample-toggle")

        this.MULTI_SAMPLE_PAGE = false
        this.MULTI_SAMPLE_DEFAULT_PAGE_SIZE = 3
        this.MULTI_SAMPLE_PAGE_SIZE = this.MULTI_SAMPLE_DEFAULT_PAGE_SIZE
        this.MULTI_SAMPLE_MAX_PAGE_SIZE = 12

        this.CHROM_DATA = null
        this.CHROM_EXP_ID = "apex-chrom-exp"
        this.CHROM_STD_ID = "apex-chrom-std"
        this.EXP_CHROM_TITLE = "Analyte Chromatogram"
        this.ISTD_CHROM_TITLE = "Internal Standard Chromatogram"
        this.CHROMATOGRAM_RETENTION_TIME_WINDOW_BUFFER = 0.5
        this.MINIMUM_NUMBER_OF_CAL_POINTS = 3

        this.ANALYTE_NAME = new URLSearchParams(window.location.search).get("name")
        this.SUBMISSION_ID = this.element.dataset.sub
        this.ANALYTE_ID = this.element.dataset.analyte
        this.ANALYTE_NAME = new URLSearchParams(window.location.search).get('name')
        console.log(`ANALYTE_NAME=${this.ANALYTE_NAME}`)
        this.IN_PROFILING_STAGE = this.element.dataset.inProfilingStage === "true"
        this.SAMPLE_FILENAMES = []
        console.log("SUBMISSION_ID, ANALYTE_ID, IN_PROFILING_STAGE", this.SUBMISSION_ID, this.ANALYTE_ID, this.IN_PROFILING_STAGE, this.element.dataset.panel, this.isLC(), this.isFia())
        console.log("Connected HTML element to Stimulus-Controller!", this.element, this)
        this.channel = this.createChromatogramChannel()
        console.log("Created websocket channel to server >", this.channel)

        console.log("Invoking get_last_sample_chromatogram...")
        this.channel.perform("get_last_sample_chromatogram", {
            "analyteId": this.ANALYTE_ID,
            "submissionId": this.SUBMISSION_ID
        })

        console.log("Invoking get_sample_filenames...")
        this.channel.perform("get_sample_filenames", {
            "submissionId": this.SUBMISSION_ID,
            "analyteId": this.ANALYTE_ID
        })

        if (this.hasCalCurvePanelTarget) {
            console.log("Invoking get_cal_curve_data...")
            this.channel.perform("get_cal_curve_data", {
                "analyteId": this.ANALYTE_ID,
                "submissionId": this.SUBMISSION_ID
            })

            this.addCalPointEventListeners()
        }

        // Draggable reference windows
        this._refWindowDragState = { active: false }
        this._onRefWindowDragMove = this._onRefWindowDragMove.bind(this)
        this._onRefWindowDragEnd = this._onRefWindowDragEnd.bind(this)
        document.addEventListener("pointermove", this._onRefWindowDragMove)
        document.addEventListener("pointerup", this._onRefWindowDragEnd)


    }

    disconnect() {
        document.removeEventListener("pointermove", this._onRefWindowDragMove)
        document.removeEventListener("pointerup", this._onRefWindowDragEnd)
        this._analyteResizable?.destroy()
        this._istdResizable?.destroy()
    }

    // ChromatogramChannel methods defined here
    createChromatogramChannel() {
        console.log("Creating ChromatogramChannel...", this)
        return consumer.subscriptions.create({
            "channel": "ChromatogramChannel",
            "submissionId": this.SUBMISSION_ID,
            "analyteId": this.ANALYTE_ID
        }, {
            THIS: this,
            connected() {
                // Called when the subscription is ready for use on the server
                this.perform("test_connection")
                this.perform("get_last_sample_chromatogram", {
                    "analyteId": this.THIS.ANALYTE_ID,
                    "submissionId": this.THIS.SUBMISSION_ID
                })
                console.log("Subscribed to ChromatogramChannel!")
                this.perform("get_sample_filenames", {
                    "submissionId": this.THIS.SUBMISSION_ID,
                    "analyteId": this.THIS.ANALYTE_ID
                })
                // console.log("In cal_curve tab", "channel")
                this.perform("get_cal_curve_data", {
                    "analyteId": this.THIS.ANALYTE_ID,
                    "submissionId": this.THIS.SUBMISSION_ID
                })
            },

            disconnected() {
                // Called when the subscription has been terminated by the server
                console.log("----- Disconnected from ChromatogramChannel!")
            },

            received(data) {
                // Called when there's incoming data on the websocket for this channel
                console.log("RECIEVED DATA:", data)

                // .status > [status-text, queued-spinner] + [reload-icon]
                if ($('.status').children().length <= 2 && this.THIS.UPDATE_ACTIONS.includes(data["action"])) { // actions that notify submission has been UPDATED in the backend (directly from worker)
                    if (window.confirm("Submission has been updated. Would you like to reload the page?")) {
                        window.location.reload()
                    } else {
                        this.THIS.subStatusTarget.innerHTML = "Status: Updated"
                        $('.status').append(`<span class="bi bi-arrow-repeat" style="align-self: center;" data-bs-toggle="tooltip" data-bs-placement="right" data-bs-title="Reload page to see updated results" data-bs-trigger="hover"></span>`)
                        // Manually init the dynamically-added tooltip with zoom-aware Popper config
                        const addedEl = $('.status').find('[data-bs-toggle="tooltip"]').last()[0];
                        if (addedEl) {
                            const zoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
                            new Tooltip(addedEl, {
                                html: true,
                                trigger: 'hover',
                                ...(zoom !== 1 && {
                                    popperConfig(defaultConfig) {
                                        return {
                                            ...defaultConfig,
                                            modifiers: [...(defaultConfig.modifiers || []), {
                                                name: 'zoomFix', enabled: true, phase: 'beforeWrite', requires: ['computeStyles'],
                                                fn({ state }) {
                                                    function adj(t) { return t ? t.replace(/translate(3d)?\(([^)]+)\)/, (_, is3d, c) => { const p = c.split(',').map(s => s.trim()); const f = p.map((s, i) => { if (i < 2) { const v = parseFloat(s); if (!isNaN(v)) return `${Math.round(v / zoom)}px`; } return s; }); return `translate${is3d || ''}(${f.join(', ')})`; }) : t; }
                                                    if (state.styles.popper?.transform) state.styles.popper.transform = adj(state.styles.popper.transform);
                                                    if (state.styles.arrow?.transform) state.styles.arrow.transform = adj(state.styles.arrow.transform);
                                                },
                                            }],
                                        };
                                    },
                                }),
                            });
                        }
                    }
                }

                // .status > [status-text] + [queued-spinner]
                if ($('.status').children().length <= 1 && this.THIS.QUEUED_ACTIONS.includes(data["action"])) {  // actions that notify submission has been QUEUED in the backend (response from channel action)
                    this.THIS.subStatusTarget.innerHTML = "Status: Queued"
                    $('.status').append(`<span class="fs-4 spinner-grow spinner-grow-sm text-warning" style="align-self: center;"></span>`)
                }

                if (data["action"] === "get_sample_filenames") {
                    console.log("Rendering sample filenames...", data)
                    this.THIS.SAMPLE_FILENAMES = data["sample_filenames"]
                }

                if (data["action"] === "get_last_sample_chromatogram") {  // initial page load
                    console.log("Rendering last sample chromatogram data...", this.THIS.EXP_CHROM_TITLE, this.THIS.ISTD_CHROM_TITLE)
                    this.THIS.CHROM_DATA = data
                    this.THIS.sampleNameTarget.textContent = data["sample_filename"].replace('.mzML', '')
                    $(this.THIS.chromatogramPanelTarget).find(".analyte-feedback").find(".og-rt").text(data.analyte.rmin === data.analyte.rmax ? "No Peak(s) Detected" : `${data.analyte.rmin != null ? parseFloat(data.analyte.rmin).toFixed(2) : ''} to ${data.analyte.rmax != null ? parseFloat(data.analyte.rmax).toFixed(2) : ''} minutes [${data.analyte.apexrt != null ? parseFloat(data.analyte.apexrt).toFixed(2) : ''}]`)
                    $(this.THIS.chromatogramPanelTarget).find(".istd-feedback").find(".og-rt").text(data.istd.rmin === data.istd.rmax ? "No Peak(s) Detected" : `${data.istd.rmin != null ? parseFloat(data.istd.rmin).toFixed(2) : ''} to ${data.istd.rmax != null ? parseFloat(data.istd.rmax).toFixed(2) : ''} minutes [${data.istd.apexrt != null ? parseFloat(data.istd.apexrt).toFixed(2) : ''}]`)

                    this.THIS.analyteGraphContainerTarget.innerHTML = ""
                    let analyteGraph = this.THIS.renderChromatogram(data["analyte"], this.THIS.analyteGraphContainerTarget, "analyte", data["area_to_use"], this.THIS.isFia(), data["sample_id"], data["sample_filename"], this.THIS.OG_HEIGHT, 0)
                    this.THIS.istdGraphContainerTarget.innerHTML = ""
                    let istdGraph = this.THIS.renderChromatogram(data["istd"], this.THIS.istdGraphContainerTarget, "istd", data["area_to_use"], this.THIS.isFia(), data["sample_id"], data["sample_filename"], this.THIS.OG_HEIGHT, 0)

                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).addClass("table-primary")
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).find(".bi.bi-eye-fill").addClass("text-primary")

                    // initial scroll to selected sample
                    $('tr[id^="analyte-results-table-sample-id-"]').each(function () {
                        if ($(this).attr('id') === `analyte-results-table-sample-id-${data["sample_id"]}`) {
                            const timer = setTimeout(() => {
                                document.getElementById($(this).attr('id')).scrollIntoView({ block: "center", inline: "center" });
                            }, 0)
                        };
                    });

                    this.THIS.MULTI_SAMPLE_LIST = [
                        {
                            "sampleID": data["sample_id"],
                            "sampleFilename": data["sample_filename"],
                            "analyte": { "chart": analyteGraph, "data": data["analyte"], "newRmin": -1, "newRmax": -1 },
                            "istd": { "chart": istdGraph, "data": data["istd"], "newRmin": -1, "newRmax": -1 },
                        }
                    ]

                    // Update auto-integration button states based on chromatogram display mode
                    this.THIS.updateAutoIntegrationButtonStates(data["analyte"], data["istd"])
                }

                if (data["action"] === "get_chromatogram_data") {  // user clicked on a sample
                    console.log("Rendering chromatogram data...", this.THIS.EXP_CHROM_TITLE, this.THIS.ISTD_CHROM_TITLE)
                    this.THIS.CHROM_DATA = data
                    this.THIS.sampleNameTarget.textContent = data["sample_filename"]
                    $(this.THIS.chromatogramPanelTarget).find(".analyte-feedback").find(".og-rt").text(data.analyte.rmin === data.analyte.rmax ? "No Peak(s) Detected" : `${data.analyte.rmin != null ? parseFloat(data.analyte.rmin).toFixed(2) : ''} to ${data.analyte.rmax != null ? parseFloat(data.analyte.rmax).toFixed(2) : ''} minutes [${data.analyte.apexrt != null ? parseFloat(data.analyte.apexrt).toFixed(2) : ''}]`)
                    $(this.THIS.chromatogramPanelTarget).find(".istd-feedback").find(".og-rt").text(data.istd.rmin === data.istd.rmax ? "No Peak(s) Detected" : `${data.istd.rmin != null ? parseFloat(data.istd.rmin).toFixed(2) : ''} to ${data.istd.rmax != null ? parseFloat(data.istd.rmax).toFixed(2) : ''} minutes [${data.istd.apexrt != null ? parseFloat(data.istd.apexrt).toFixed(2) : ''}]`)

                    let analyteGraph = this.THIS.analyteGraphContainerTarget.innerHTML = ""
                    analyteGraph = this.THIS.renderChromatogram(data["analyte"], this.THIS.analyteGraphContainerTarget, "analyte", data["area_to_use"], this.THIS.isFia(), data["sample_id"], data["sample_filename"], this.THIS.OG_HEIGHT, 0)
                    let istdGraph = this.THIS.istdGraphContainerTarget.innerHTML = ""
                    istdGraph = this.THIS.renderChromatogram(data["istd"], this.THIS.istdGraphContainerTarget, "istd", data["area_to_use"], this.THIS.isFia(), data["sample_id"], data["sample_filename"], this.THIS.OG_HEIGHT, 0)

                    $(".analyte-results-table-sample").each(function () {
                        $(this).removeClass("table-primary")
                        $(this).find(".bi.bi-eye-fill").removeClass("text-primary")
                    })
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).addClass("table-primary")
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).find(".bi.bi-eye-fill").addClass("text-primary")
                    this.THIS.MULTI_SAMPLE_LIST = [  // reset multi-sample list
                        {
                            "sampleID": data["sample_id"],
                            "sampleFilename": data["sample_filename"],
                            "analyte": { "chart": analyteGraph, "data": data["analyte"], "newRmin": -1, "newRmax": -1 },
                            "istd": { "chart": istdGraph, "data": data["istd"], "newRmin": -1, "newRmax": -1 },
                        }
                    ]

                    // Update auto-integration button states based on chromatogram display mode
                    this.THIS.updateAutoIntegrationButtonStates(data["analyte"], data["istd"])
                }

                if (data["action"] === "get_chromatogram_data_multi_sample") {
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).addClass("table-primary")
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).find(".bi.bi-eye-fill").addClass("text-primary")
                    $(`#analyte-results-table-sample-id-${data["sample_id"]}`).find(".td-view.view-chromatogram-icon").addClass("glow-cell-selected")
                    this.THIS.appendSampleChromatograms(data)
                }

                if (data["action"] === "get_chromatogram_data_multi_sample_page") {
                    data["samples"].forEach((sample, i) => {
                        $(`#analyte-results-table-sample-id-${sample["sample_id"]}`).addClass("table-primary")
                        $(`#analyte-results-table-sample-id-${sample["sample_id"]}`).find(".bi.bi-eye-fill").addClass("text-primary")
                        $(`#analyte-results-table-sample-id-${sample["sample_id"]}`).find(".td-view.view-chromatogram-icon").addClass("glow-cell-selected")
                        if (i == 0) {
                            this.THIS.appendSampleChromatograms(sample, true)
                        }
                        else {
                            this.THIS.appendSampleChromatograms(sample)
                        }
                    })
                    console.log("SAMPLES", data["samples"])
                }

                if (data["action"] === "get_cal_curve_data" && this.THIS.isLC()) {
                    console.log("Rendering calibration curve data...", data, this.THIS.calCurveGraphTarget)
                    this.THIS.CAL_CURVE_DATA = data
                    this.THIS.CAL_CURVE_PLOT = this.THIS.renderCalibrationCurve(data["cal_curve"], this.THIS.calCurveGraphTarget, new URLSearchParams(window.location.search).get("name"))
                }
            }
        });

    }

    disableAllActionButtons() {
        $("#reintegrate-btn").prop("disabled", true)
        $("#recalibrate-btn").prop("disabled", true)
    }
    /**
     * Convert an HTML element (e.g., D3 container div) to an image and download it.
     *
     * @param {HTMLElement|string} elOrSelector - Element or CSS selector for the graph container.
     * @param {Object} opts
     * @param {string} [opts.filename="d3-graph.png"] - Download filename.
     * @param {"png"|"jpeg"|"svg"} [opts.format="png"] - Output image format.
     * @param {number} [opts.scale=2] - Render scale (2+ for sharper text).
     * @param {string} [opts.backgroundColor="#ffffff"] - Background color. Use null for transparent.
     * @param {number} [opts.quality=0.92] - JPEG quality (0-1). Ignored for PNG/SVG.
     * @param {(node: HTMLElement) => HTMLElement} [opts.prepare] - Optional hook to mutate a cloned node pre-render.
     */
    async downloadElementAsImage(elOrSelector, opts = {}) {
        const {
            filename = "d3-graph.png",
            format = "png",
            scale = 2,
            backgroundColor = "#ffffff",
            quality = 0.92,
            prepare,
        } = opts;

        const node =
            typeof elOrSelector === "string"
                ? document.querySelector(elOrSelector)
                : elOrSelector;

        if (!node) throw new Error("downloadElementAsImage: element not found");

        // Ensure webfonts are ready (helps text render correctly)
        if (document.fonts && document.fonts.ready) {
            try { await document.fonts.ready; } catch { }
        }

        // Compute size from layout (important if node uses responsive sizing)
        const rect = node.getBoundingClientRect();
        const width = Math.ceil(rect.width);
        const height = Math.ceil(rect.height);

        // html-to-image uses the DOM as-is; we can force a consistent background + scaling.
        const common = {
            backgroundColor: backgroundColor ?? undefined,
            width,
            height,
            style: {
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                width: `${width}px`,
                height: `${height}px`,
            },
            // If your D3 graph loads external images, this helps (still subject to CORS).
            cacheBust: true,
            // Optional: tweak the cloned node before rendering (e.g., remove tooltips, hover states)
            onClone: async (clonedDoc) => {
                if (!prepare) return;
                const clonedNode = clonedDoc.querySelector(
                    node.id ? `#${CSS.escape(node.id)}` : null
                );
                if (clonedNode) prepare(clonedNode);
            },
        };

        let dataUrl;
        if (format === "svg") {
            dataUrl = await htmlToImage.toSvg(node, common);
        } else if (format === "jpeg") {
            dataUrl = await htmlToImage.toJpeg(node, { ...common, quality });
        } else {
            dataUrl = await htmlToImage.toPng(node, common);
        }

        // Download
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    async downloadChromatogram(event) {
        event.preventDefault();

        const target = event.target
        const chartType = target.dataset.chartType
        console.log(`clicked download`, target, chartType)

        const mainParentContainer = target.closest(".graph-container");
        console.log('parent:', mainParentContainer)
        const targetChartContainer = mainParentContainer.querySelector(`#${chartType}-graph-container`)
        console.log('targetChartContainer:', targetChartContainer)

        let analyteName = new URLSearchParams(window.location.search).get("name")
        analyteName = chartType == "analyte" ? analyteName : analyteName + "-ISTD"

        let sampleNameOG = mainParentContainer.querySelector('.sample-filename').textContent
        sampleNameOG = sampleNameOG.includes('.mzML') ? sampleNameOG : sampleNameOG + '.mzML'  // ensure .mzML extension is included for consistent filename parsing, since some places show it and some don't
        let sampleName = sampleNameOG.split('_')
        sampleName = sampleName.slice(5, sampleName.length).join('_')
        let filename = this.sanitizeFilename(`${analyteName}.${sampleName}.${this.imageDownloadFormat}`)

        try {
            target.disabled = true;
            if (["png", "svg", "jpeg"].includes(this.imageDownloadFormat)) {
                await this.downloadElementAsImage(targetChartContainer, {
                    filename: filename,
                    format: this.imageDownloadFormat,
                    scale: 0.9,
                    backgroundColor: "#fff",
                });
            } else if (this.imageDownloadFormat == "csv") {
                let chromData = this.MULTI_SAMPLE_LIST.filter(s => s["sampleFilename"] === sampleNameOG)
                if (chromData.length > 1) {
                    throw new Error(`Multiple chromatograms found for this sample file name ${sampleNameOG}//${JSON.stringify(this.MULTI_SAMPLE_LIST)} - cannot determine which one to download as CSV.`)
                } else if (chromData.length == 0) {
                    console.log(`sampleName=${sampleNameOG}, sampleNameOG=${sampleNameOG}, MULTI_SAMPLE_LIST=`, this.MULTI_SAMPLE_LIST)
                    chromData = this.MULTI_SAMPLE_LIST.filter(s => s["sampleFilename"].includes(sampleName))  // try partial match if exact match with .mzML extension doesn't work (handles cases where .mzML is not shown in the filename)
                    if (chromData.length > 1) {
                        throw new Error(`Multiple chromatograms found with sample file name including ${sampleNameOG}//${JSON.stringify(this.MULTI_SAMPLE_LIST)} - cannot determine which one to download as CSV.`)
                    } else if (chromData.length == 0) {
                        if (this.MULTI_SAMPLE_LIST.length == 1) {
                            console.log('No chromatogram data found for this sample:', this.MULTI_SAMPLE_LIST)
                            chromData = this.MULTI_SAMPLE_LIST  // if there's only one chromatogram in the list, just use that one even if the filename doesn't match (handles edge case where .mzML is not shown and the rest of the filename is not consistent for some reason)
                        } else {
                            throw new Error(`No chromatogram data found for sample file name ${sampleNameOG}//${JSON.stringify(this.MULTI_SAMPLE_LIST)}. Cannot download CSV.`)
                        }
                    }
                }
                chromData = chromData[0]?.[chartType].data
                console.log("chromData for CSV download:", chromData)
                const headers = ["Retention Time", "Intensity"]
                let rows = [headers]
                chromData["values"].forEach((point, i) => {
                    rows.push([point.x, point.y])
                })
                const csvContent = rows.map(e => e.join(",")).join("\n");
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();   
            }
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            target.disabled = false;
        }
    }

    sanitizeFilename(name, {
        replacement = "_",
        maxLength = 255,
    } = {}) {
        if (!name) return "file";

        return name
            .normalize("NFKD")                 // normalize accents
            .replace(/[\u0300-\u036f]/g, "")   // strip diacritics
            .replace(/[<>:"/\\|?*\x00-\x1F]/g, replacement) // illegal chars
            .replace(/\s+/g, replacement)     // spaces → _
            .replace(/_+/g, "_")               // collapse repeats
            .replace(/^_+|_+$/g, "")           // trim
            .slice(0, maxLength)               // enforce length
            || "file";
    }

    setImageDownloadFormat() {
        this.imageDownloadFormat = this.formatGroupTarget.querySelector('input[name="chromDownloadFormat"]:checked')?.value || "png";
    }

    toggleRefWindow(event) {
        const chartType = event.currentTarget.dataset.chartType
        const win = chartType === "analyte" ? this.analyteRefWindowTarget : this.istdRefWindowTarget
        const isVisible = win.style.display !== "none"
        console.log(`Toggling reference window for ${chartType} (currently ${isVisible ? "visible" : "hidden"})`, win)
        if (isVisible) {
            win.style.display = "none"
        } else {
            // add a transition to make it look like its fading in from the left of the screen
            win.style.opacity = 0
            win.style.transform = "translateX(-20px)"
            win.style.display = "block"
            this._bringRefWindowToFront(win)
            setTimeout(() => {
                win.style.transition = "transform 0.3s ease, opacity 0.3s ease"
                win.style.transform = "translateX(0)"
                win.style.opacity = 1
            }, 10)
            this._bringRefWindowToFront(win)
            this._setupResizableWindow(chartType)
        }
    }

    _setupResizableWindow(chartType, win) {
        // Resizable reference windows
        const windowElement = chartType === "analyte" ? this.analyteRefWindowTarget : this.istdRefWindowTarget
        const resizableWin = new Resizable(windowElement, {
            handles: "s, se, e, ne, n, nw, w, sw",
            draggable: false,
            css3: false,
            within: window,
        })
        if (chartType === "analyte") {
            this._analyteResizable?.destroy()
            this._analyteResizable = resizableWin
        } else {
            this._istdResizable?.destroy()
            this._istdResizable = resizableWin
        }
    
    }

    closeRefWindow(event) {
        event.stopPropagation()
        const chartType = event.currentTarget.dataset.chartType
        const win = chartType === "analyte" ? this.analyteRefWindowTarget : this.istdRefWindowTarget
        win.style.display = "none"
    }

    startRefWindowDrag(event) {
        if (event.target.closest(".btn-close")) return  // don't drag when clicking close button
        const handle = event.currentTarget
        const win = handle.closest(".floating-ref-window")
        if (!win) return
        const rect = win.getBoundingClientRect()
        // getBoundingClientRect() returns visual px; style.left/top on a position:fixed
        // element must be in CSS layout px. Compute the ratio once so deltas stay correct
        // even when body { zoom } is applied (e.g. zoom: 0.8 used elsewhere in the app).
        const zoom = rect.width / win.offsetWidth || 1
        this._refWindowDragState = {
            active: true,
            window: win,
            handle: handle,
            startX: event.clientX,
            startY: event.clientY,
            origLeft: rect.left / zoom,   // convert visual px → CSS layout px
            origTop: rect.top / zoom,
            zoom,
        }
        this._bringRefWindowToFront(win)
        handle.style.cursor = "grabbing"
        event.preventDefault()
    }

    _onRefWindowDragMove(event) {
        if (!this._refWindowDragState?.active) return
        const { window: win, startX, startY, origLeft, origTop, zoom } = this._refWindowDragState
        // Divide visual-px delta by zoom to get the CSS-px delta
        win.style.left = `${origLeft + (event.clientX - startX) / zoom}px`
        win.style.top = `${origTop + (event.clientY - startY) / zoom}px`
    }

    _onRefWindowDragEnd(event) {
        if (!this._refWindowDragState?.active) return
        this._refWindowDragState.handle.style.cursor = "grab"
        this._refWindowDragState.active = false
    }

    _bringRefWindowToFront(win) {
        this._refWindowZIndex = Math.max(this._refWindowZIndex || 2000, 2000) + 1
        win.style.zIndex = this._refWindowZIndex
    }

    // Shared checkbox-icon toggle: flips a boolean property and swaps the icon class
    _toggleCheckbox(event, propertyName) {
        this[propertyName] = !this[propertyName];
        const icon = $(event.currentTarget).find('span');
        icon.toggleClass('bi-dash-square-dotted', !this[propertyName]);
        icon.toggleClass('bi-check-square-fill', this[propertyName]);
    }

    toggleReintegrateAllSamples(event) {
        this._toggleCheckbox(event, 'reintegrateAllSamples');
    }

    toggleAutoAdjustAnalyte(event) {
        if (event.currentTarget.disabled) return;
        this._toggleCheckbox(event, 'autoAdjust_analyte');
    }

    toggleAutoAdjustISTD(event) {
        if (event.currentTarget.disabled) return;
        this._toggleCheckbox(event, 'autoAdjust_istd');
    }

    toggleRecalibrateAllAnalytes(event) {
        this._toggleCheckbox(event, 'recalibrateAllAnalytes');
    }

    /**
     * Update auto-integration button states based on chromatogram display mode.
     * If a chromatogram is showing deconvolution (not peak picking), disable its auto-integration option.
     * @param {Object} analyteData - The analyte chromatogram data
     * @param {Object} istdData - The ISTD chromatogram data
     */
    updateAutoIntegrationButtonStates(analyteData, istdData) {
        // Determine if analyte is showing deconvolution
        const analyteHasDeconvolution = analyteData?.has_deconvolution || false
        const analyteIntegrationType = analyteData?.integration_type
        const analyteIsExplicitlyPeakPicking = analyteIntegrationType === 'peak_picking'
        const analyteShowingDeconvolution = analyteHasDeconvolution && !analyteIsExplicitlyPeakPicking

        // Determine if ISTD is showing deconvolution
        const istdHasDeconvolution = istdData?.has_deconvolution || false
        const istdIntegrationType = istdData?.integration_type
        const istdIsExplicitlyPeakPicking = istdIntegrationType === 'peak_picking'
        const istdShowingDeconvolution = istdHasDeconvolution && !istdIsExplicitlyPeakPicking

        console.log("[updateAutoIntegrationButtonStates] analyteShowingDeconvolution:", analyteShowingDeconvolution, "istdShowingDeconvolution:", istdShowingDeconvolution)

        // Update Analyte auto-integration button
        if (this.hasAutoIntegrationAnalyteBtnTarget) {
            const btn = this.autoIntegrationAnalyteBtnTarget
            if (analyteShowingDeconvolution) {
                // Disable and uncheck - showing deconvolution
                btn.disabled = true
                btn.classList.add("disabled", "text-muted")
                btn.style.opacity = "0.5"
                btn.style.cursor = "not-allowed"
                this.autoAdjust_analyte = false
                $(btn).find('span').removeClass("bi-check-square-fill")
                $(btn).find('span').addClass("bi-dash-square-dotted")
            } else {
                // Enable and check - showing peak picking
                btn.disabled = false
                btn.classList.remove("disabled", "text-muted")
                btn.style.opacity = "1"
                btn.style.cursor = "pointer"
                this.autoAdjust_analyte = true
                $(btn).find('span').removeClass("bi-dash-square-dotted")
                $(btn).find('span').addClass("bi-check-square-fill")
            }
        }

        // Update ISTD auto-integration button
        if (this.hasAutoIntegrationIstdBtnTarget) {
            const btn = this.autoIntegrationIstdBtnTarget
            if (istdShowingDeconvolution) {
                // Disable and uncheck - showing deconvolution
                btn.disabled = true
                btn.classList.add("disabled", "text-muted")
                btn.style.opacity = "0.5"
                btn.style.cursor = "not-allowed"
                this.autoAdjust_istd = false
                $(btn).find('span').removeClass("bi-check-square-fill")
                $(btn).find('span').addClass("bi-dash-square-dotted")
            } else {
                // Enable and check - showing peak picking
                btn.disabled = false
                btn.classList.remove("disabled", "text-muted")
                btn.style.opacity = "1"
                btn.style.cursor = "pointer"
                this.autoAdjust_istd = true
                $(btn).find('span').removeClass("bi-dash-square-dotted")
                $(btn).find('span').addClass("bi-check-square-fill")
            }
        }
    }

    switchToPeakPickingMode(event) {
        console.log("Switching to peak picking mode")
        this.integrationMode = "peak_picking"

        // Update button states
        if (this.hasPeakPickingModeBtnTarget && this.hasDeconvolutionModeBtnTarget) {
            this.peakPickingModeBtnTarget.classList.add("active")
            this.deconvolutionModeBtnTarget.classList.remove("active")
        }

        // Show peak picking options
        if (this.hasPeakPickingOptionsTarget) {
            this.peakPickingOptionsTarget.style.display = "block"
        }
    }

    switchToDeconvolutionMode(event) {
        console.log("Switching to deconvolution mode")
        this.integrationMode = "deconvolution"

        // Update button states
        if (this.hasPeakPickingModeBtnTarget && this.hasDeconvolutionModeBtnTarget) {
            this.peakPickingModeBtnTarget.classList.remove("active")
            this.deconvolutionModeBtnTarget.classList.add("active")
        }

        // Hide peak picking options
        if (this.hasPeakPickingOptionsTarget) {
            this.peakPickingOptionsTarget.style.display = "none"
        }
    }

    cloneFirstSampleNewRTs() {
        // returns a copy of the first sample's new RTs for all other samples
        let newMultiSampleList = []
        let firstSample = this.MULTI_SAMPLE_LIST[0]
        console.log("FIRST SAMPLE", firstSample)

        let newSample;
        this.SAMPLE_FILENAMES.forEach((sample_filename, idx) => {
            newSample = {
                "sampleID": firstSample["sampleID"],
                "sampleFilename": sample_filename,
                "analyte": {
                    "chart": null,
                    "newRmin": firstSample["analyte"]["newRmin"],
                    "newRmax": firstSample["analyte"]["newRmax"]
                },
                "istd": {
                    "chart": null,
                    "newRmin": firstSample["istd"]["newRmin"],
                    "newRmax": firstSample["istd"]["newRmax"]
                }
            }
            newMultiSampleList.push(newSample)
        })

        console.log("NEW newMultiSampleList", newMultiSampleList)
        return newMultiSampleList;
    }

    reintegrateRTOld() {
        let payload = {
            // "isMultiSample": this.MULTI_SAMPLE,
        }
        if (this.reintegrateAllSamples) this.cloneFirstSampleNewRTs()
        let proceed = false;
        if (this.isLC()) {
            let analyte_rts, istd_rts;
            console.log("MULTI_SAMPLE_LIST >> reintegrateRT", this.MULTI_SAMPLE_LIST)
            this.MULTI_SAMPLE_LIST.forEach((sample, idx) => {
                console.log("SAMPLE", sample)
                analyte_rts = [sample["analyte"]["newRmin"], sample["analyte"]["newRmax"]]
                istd_rts = [sample["istd"]["newRmin"], sample["istd"]["newRmax"]]
                payload[sample["sampleFilename"]] = {
                    "idx": idx,
                    "analyte": {
                        "rmin": analyte_rts[0],
                        "rmax": analyte_rts[1]
                    },
                    "istd": {
                        "rmin": istd_rts[0],
                        "rmax": istd_rts[1]
                    }
                }
                if (!this.MULTI_SAMPLE || !this.MULTI_SAMPLE_PAGE || this.reintegrateAllSamples) {
                    if (idx === 0 && (analyte_rts.includes(-1) && istd_rts.includes(-1))) {
                        alert(`Reintegration cannot be applied to all samples. No new retention times detected. Please select at least one new retention time (one for experimental and/or one for internal standard) on the FIRST [${this.MULTI_SAMPLE_LIST[0]["sampleFilename"]}] sample.`)
                        proceed = false
                    }
                }
                if (!this.reintegrateAllSamples) {
                    if (idx > 0 && (!analyte_rts.includes(-1) || !istd_rts.includes(-1))) {
                        console.log("MULTI_SAMPLE_LIST", idx, this.MULTI_SAMPLE_LIST)
                        alert(`Reintegration cannot be applied to all samples. Multiple new retention times detected. Please only select new retention time(s) (one for experimental and/or one for internal standard) on the FIRST [${this.MULTI_SAMPLE_LIST[0]["sampleFilename"]}] sample ONLY.`)
                        proceed = false
                    }
                }
                if (!this.reintegrateAllSamples) {
                    if (!analyte_rts.includes(-1) || !istd_rts.includes(-1)) {
                        proceed = true
                    }
                }
                if (this.reintegrateAllSamples && idx === 0) {
                    proceed = true
                }
            })
            if (!proceed) {
                alert("New retention times can be selected by dragging across the X axis of a chromatogram.")
            }

        } else if (this.isFia()) {
            let fiaMinRt = parseFloat(this.fiaMinRtTarget.value)
            let fiaMaxRt = parseFloat(this.fiaMaxRtTarget.value)
            console.log("FIA RTs", fiaMinRt, fiaMaxRt)
            if (!fiaMinRt || !fiaMaxRt) {
                alert("Please ensure that both the minimum and maximum retention times are filled out.")
            } else if (fiaMinRt >= fiaMaxRt) {
                alert("Please ensure that the minimum retention time is less than the maximum retention time.")
            } else {
                payload["fia"] = {
                    "rmin": fiaMinRt,
                    "rmax": fiaMaxRt
                }
                proceed = true
            }
        }


        proceed && this.channel.perform("reintegrate_rt", {
            "analyteId": this.ANALYTE_ID,
            "submissionId": this.SUBMISSION_ID,
            "payload": payload
        })
    }

    reintegrateRT() {
        let payload = {}

        if (this.isLC()) {
            let { valid, errorTypes } = this.validateMultiSampleList()
            console.log("VALIDATE MULTI_SAMPLE_LIST", valid, errorTypes)
            if (valid) {
                payload = this.generateNewRtPayload()
                console.log("[REINTEGRATE] Sending with integrationMode:", this.integrationMode, "hasDeconvolution:", this.hasDeconvolution)
                this.channel.perform("reintegrate_rt", {
                    "analyteId": this.ANALYTE_ID,
                    "submissionId": this.SUBMISSION_ID,
                    "payload": payload,
                    "applyToAllSamples": this.reintegrateAllSamples,
                    "autoAdjust_analyte": this.autoAdjust_analyte,
                    "autoAdjust_istd": this.autoAdjust_istd,
                    "integrationMode": this.integrationMode,
                    "hasDeconvolution": this.hasDeconvolution
                })
                this.disableAllActionButtons()
            } else {
                if (errorTypes[0] === 0) alert(`Reintegration cannot be applied to all samples. No new retention times detected. Please select at least one new retention time (one for experimental and/or one for internal standard) on the FIRST [${this.MULTI_SAMPLE_LIST[0]["sampleFilename"]}] sample.`)
                if (errorTypes[1] === 0) alert(`Reintegration cannot be applied to all samples. Multiple new retention times detected. Please only select new retention time(s) (one for experimental and/or one for internal standard) on the FIRST [${this.MULTI_SAMPLE_LIST[0]["sampleFilename"]}] sample ONLY.`)
                if (errorTypes[2] === 0) alert("Reintegration cannot be applied. No new retention times detected. New retention times can be selected by dragging across the X axis of a chromatogram.")
            }
        } else if (this.isFia()) {
            let fiaMinRt = this.fiaMinRtTarget.value ? parseFloat(this.fiaMinRtTarget.value) : "NaN"
            let fiaMaxRt = this.fiaMaxRtTarget.value ? parseFloat(this.fiaMaxRtTarget.value) : "NaN"
            console.log("FIA RTs", fiaMinRt, fiaMaxRt, typeof fiaMinRt, typeof fiaMaxRt)
            if (fiaMinRt === "NaN" || fiaMaxRt === "NaN") {
                alert("Please ensure that both the minimum and maximum retention times are filled out.")
            } else if (fiaMinRt >= fiaMaxRt) {
                alert("Please ensure that the minimum retention time is less than the maximum retention time.")
            } else {
                payload["fia"] = {
                    "rmin": fiaMinRt,
                    "rmax": fiaMaxRt
                }
                this.channel.perform("reintegrate_rt", {
                    "analyteId": this.ANALYTE_ID,
                    "submissionId": this.SUBMISSION_ID,
                    "payload": payload
                })
                this.disableAllActionButtons()
            }
        }

    }

    getSampleType(fileBasename) {
        if (fileBasename.includes("Cal_cal")) return "cal"
        if (fileBasename.includes("QC_qc")) return "qc"
        if (fileBasename.includes("Double_Blank_dbl_blk")) return "dbl_blk"
        if (fileBasename.includes("Blank_blk")) return "blk"
        if (fileBasename.includes("Exp")) return "exp"
    }

    getCalIdx(fileBasename) {
        if (this.getSampleType(fileBasename) === "cal") {
            return parseInt(fileBasename.split("_").at(-2))
        }
        return -1
    }

    calSampleWasUnselected(fileBasename) {
        // returns true if cal idx was unselected in CAL_CURVE_DATA
        if (this.getSampleType(fileBasename) === CAL) {
            let calIdx = this.getCalIdx(fileBasename)
            this.CAL_CURVE_DATA.cal_points.forEach((point) => {
                if (point["idx"] === calIdx) {
                    return !point["selected"]
                }
            })
        }
        return false
    }

    generateNewRtPayload() {  // creates payload from MULTI_SAMPLE_LIST for reintegrate_rt
        let payload = {}
        let finalList;

        if (this.reintegrateAllSamples) finalList = this.cloneFirstSampleNewRTs()
        else finalList = this.MULTI_SAMPLE_LIST

        console.log("FINAL LIST", finalList)
        finalList.forEach((sample, idx) => {
            let analyte_rts = [sample["analyte"]["newRmin"], sample["analyte"]["newRmax"]]
            let istd_rts = [sample["istd"]["newRmin"], sample["istd"]["newRmax"]]
            // if analyte name in HAS_NO_ISTDS: set istd_rts to [-1, -1] to avoid sending new RTs for ISTD when it doesn't exist
            console.log("HAS_NO_ISTDS.values", Object.values(this.HAS_NO_ISTDS), "ANALYTE_NAME", this.ANALYTE_NAME)
            if (Object.values(this.HAS_NO_ISTDS).includes(this.ANALYTE_NAME)) {
                istd_rts = [-1, -1]
            }
            let file_basename = sample["sampleFilename"].split('.').slice(0, -1).join('.')
            if (Object.values(this.HAS_NO_ISTDS).includes(this.ANALYTE_NAME)) {
                // reset all istd rts to -1 since these don't have any istd
                istd_rts = [-1, -1]
            }
            // if (this.calSampleWasUnselected(file_basename)) return  // skip unselected cal samples
            payload[file_basename] = {
                "idx": idx,
                "analyte": {
                    "rmin": analyte_rts[0],
                    "rmax": analyte_rts[1]
                },
                "istd": {
                    "rmin": istd_rts[0],
                    "rmax": istd_rts[1]
                }
            }
        })

        // console.log("ApplyNewRT.PAYLOAD", payload)
        return payload
    }

    validateMultiSampleList() {
        let valid;
        let errorTypes = [1, 1, 0]  // [firstSampleHasNewRTs(reintegrateAllSample=True), allOtherSamplesHaveNoNewRTs(reintegrateAllSample=True), atLeastOneSampleHasNewRts(reintegrateAllSample=False)]  1 = valid, 0 = invalid

        if (this.reintegrateAllSamples) {  // ONLY first sample should have new RTs; all other samples should have no new RTs
            valid = true
            let firstSample = this.MULTI_SAMPLE_LIST[0]
            let firstSampleAnalyteRTs = [firstSample["analyte"]["newRmin"], firstSample["analyte"]["newRmax"]]
            let firstSampleIstdRTs = [firstSample["istd"]["newRmin"], firstSample["istd"]["newRmax"]]
            if (firstSampleAnalyteRTs.includes(-1) && firstSampleIstdRTs.includes(-1)) {
                valid = false
                errorTypes[0] = 0
            }
            console.log("this.reintegrateAllSamples", this.reintegrateAllSamples)
            let extra = this.numberOfExtraSamplesWithNewRTs()
            if (extra) {  // if extra === 0 => no extra samples with new RTs => valid
                valid = false
                errorTypes[1] = 0
            }
        } else {
            console.log("this.MULTI_SAMPLE_LIST", this.MULTI_SAMPLE_LIST)
            valid = false
            for (let sample of this.MULTI_SAMPLE_LIST) {
                if (
                    (sample["analyte"]["newRmin"] !== -1 && sample["analyte"]["newRmax"] !== -1) ||
                    (sample["istd"]["newRmin"] !== -1 && sample["istd"]["newRmax"] !== -1)
                ) {
                    errorTypes[2] = 1;
                    valid = true;
                    break;
                }
            }
        }

        console.log("VALIDITY", valid, errorTypes, this.reintegrateAllSamples, this.MULTI_SAMPLE_LIST)
        return { valid, errorTypes }
    }

    numberOfExtraSamplesWithNewRTs() {
        let count = 0
        this.MULTI_SAMPLE_LIST.forEach((sample, idx) => {
            if (idx > 0) {  // skip first sample because it's the main sample and SHOULD have new RTs
                if (sample["analyte"]["newRmin"] !== -1 || sample["analyte"]["newRmax"] !== -1 || sample["istd"]["newRmin"] !== -1 || sample["istd"]["newRmax"] !== -1) {
                    count++
                }
            }
        })
        return count
    }

    toggleCalPointHover(event, point, markerEle) {
        let selected = this.isTrue(point.dataset.ccSelected)
        let toggle = this.isTrue(point.dataset.ccToggle)
        if (event === "mouseenter") {
            markerEle.setAttribute("r", "20")
            markerEle.setAttribute("stroke-width", "1")
            // markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_ALT)
            // markerEle.setAttribute("filter", "")
        } else if (event === "mouseleave") {
            // markerEle.setAttribute("filter", "")
            if (selected === toggle) {
                if (selected) {
                    markerEle.setAttribute("r", "10")
                    markerEle.setAttribute("stroke-width", "1")
                    markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR)
                } else {
                    markerEle.setAttribute("r", "5")
                    markerEle.setAttribute("stroke-width", "1")
                    markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_UNFILLED)
                }
            } else {  // user has toggled the point
                if (toggle) {
                    markerEle.setAttribute("r", this.isTrue(point.dataset.ccSelected) ? "10" : "5")
                    markerEle.setAttribute("stroke-width", "1")
                    markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_SELECTED)
                } else {
                    markerEle.setAttribute("r", this.isTrue(point.dataset.ccSelected) ? "10" : "5")
                    markerEle.setAttribute("stroke-width", "1")
                    markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_UNFILLED)
                }
            }

        }
    }

    toggleCalPointSelection(point, markerEle) {
        point.dataset.ccToggle = this.isTrue(point.dataset.ccToggle) ? '0' : '1'
        $(point).find(".cc-point-toggle-icon").toggleClass("bi-check-lg text-dark")
        $(point).find(".cc-point-toggle-icon").toggleClass("bi-dash-lg text-dark")

        if (parseInt(point.dataset.ccToggle)) {
            markerEle.setAttribute("r", this.isTrue(point.dataset.ccSelected) ? "10" : "5")
            markerEle.setAttribute("stroke-width", "1")
            markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_SELECTED)
            // markerEle.setAttribute("filter", "")
        }

        if (!parseInt(point.dataset.ccToggle)) {
            markerEle.setAttribute("r", "5")
            markerEle.setAttribute("stroke-width", "1")
            markerEle.setAttribute("fill", this.CAL_CURVE_MARKER_COLOR_UNFILLED)
            // markerEle.setAttribute("filter", "")
        }

    }

    addCalPointEventListeners() {
        let calPoints = this.calCurvePointsListTarget.querySelectorAll(".cal-curve-point")
        calPoints.forEach((point) => {

            if (!this.IN_PROFILING_STAGE) {
                point.addEventListener("click", (event) => {
                    let markerEle = this.CAL_CURVE_PLOT.toggleDataPointSelection(0, point.dataset.ccIdx - 1)
                    console.log(point)
                    this.toggleCalPointSelection(point, markerEle)
                    console.log(point)

                })
            }

            point.addEventListener("mouseenter", (event) => {
                let markerEle = this.CAL_CURVE_PLOT.toggleDataPointSelection(0, point.dataset.ccIdx - 1)
                this.toggleCalPointHover("mouseenter", point, markerEle)
            })

            point.addEventListener("mouseleave", (event) => {
                let markerEle = this.CAL_CURVE_PLOT.toggleDataPointSelection(0, point.dataset.ccIdx - 1)
                this.toggleCalPointHover("mouseleave", point, markerEle)
            })
        })
    }

    recalibrateAnalyte(event) {
        if (this.IN_PROFILING_STAGE) {
            alert("Analyte can be recalibrated only during preprocessing stage (before profiling of experimental samples).")
            return
        }
        let payload = {
        }
        let calPoints = this.calCurvePointsListTarget.querySelectorAll(".cal-curve-point")
        let proceed = false
        let validSelectedCalPointCount = 0
        calPoints.forEach((point) => {
            if (!proceed && point.dataset.ccSelected != point.dataset.ccToggle) proceed = true  // if at least one point has been toggled
            payload[point.dataset.ccIdx] = {
                "selected": this.isTrue(point.dataset.ccToggle),
                "x": parseFloat(point.dataset.ccX),
                "y": parseFloat(point.dataset.ccY),
                "chromatogramValidity": this.isTrue(point.dataset.ccChromatogramValidity),
            }
            if (this.isBoolean(true, point.dataset.ccValidChromatogram) && this.isBoolean(true, point.dataset.ccToggle)) validSelectedCalPointCount++
        })

        if (!proceed) {
            alert("Please toggle at least one calibration point selection first by clicking on the list of points next to the graph.")
        } else {
            if (validSelectedCalPointCount < this.MINIMUM_NUMBER_OF_CAL_POINTS) {
                alert(`Please ensure that at least ${this.MINIMUM_NUMBER_OF_CAL_POINTS} valid calibration points are selected before recalibrating. Invalid calibration points can be converted to valid by manually reintegrating retention times on the chromatogram.`)
            } else {
                this.channel.perform("recalibrate_analyte", {
                    "analyteId": this.ANALYTE_ID,
                    "submissionId": this.SUBMISSION_ID,
                    "calPoints": payload,
                    "applyToAllAnalytes": this.recalibrateAllAnalytes,
                })
                this.disableAllActionButtons()
            }
        }
    }

    isBoolean(val, target) {
        if (val === true) {
            return [true, "true", "True", "1", 1,].includes(target)
        } else {
            return [false, "false", "False", "0", 0].includes(target)
        }
    }

    toggleMultiSample(event) {
        if (this.MULTI_SAMPLE_PAGE) {
            this.MULTI_SAMPLE_PAGE = false;  // Disable MULTI_SAMPLE_PAGE
            this.unhighlightBookmark("multi-sample-page-toggle")
        }
        this.MULTI_SAMPLE = !this.MULTI_SAMPLE
        if (this.MULTI_SAMPLE) {
            console.log(event.currentTarget)
            console.log(this.CHROM_DATA)
            this.appendSampleChromatograms(this.CHROM_DATA, true)
            this.highlightBookmark("multi-sample-toggle")
            // console.log("GRAPH CONTAINERS", graphContainers)

        } else {
            this.MULTI_SAMPLE_LIST = [this.MULTI_SAMPLE_LIST[0]]  // reset multi-sample list to only the first element

            // this.updateGraphHeight(this.OG_HEIGHT, this.HEIGHT_MULTIPLIER)
            this.unhighlightBookmark("multi-sample-toggle")
            this.appendSampleChromatograms(this.CHROM_DATA, true)
        }
        // console.log("MULTI_SAMPLE", this.MULTI_SAMPLE, this.MULTI_SAMPLE_LIST)
    }

    removeAllMultiSampleContainers() {
        let container = this.chromatogramPanelTarget
        while (container.firstChild) {
            container.removeChild(container.firstChild)
        }
        this.MULTI_SAMPLE_LIST = []
    }

    removeMultiSampleContainers() {
        let original = this.MULTI_SAMPLE_LIST[0]
        let graphContainers = this.chromatogramPanelTarget.childNodes
        let toRemove = []
        // console.log("GRAPH CONTAINERS", graphContainers)
        graphContainers.forEach((container, idx) => {
            if (idx > 0) {
                // console.log("REMOVING", container)
                toRemove.push(container)
                // console.log("REMOVED", toRemove)
            }
        })
        toRemove.forEach((container) => {
            container.remove()
        })

        // graphContainers.filter((container, idx) => idx > 0).map((container) => container.remove())
    }


    highlightBookmark(buttonID) {
        console.log("HIGHLIGHT BOOKMARK")
        const chromatogramTab = document.getElementById("chromatograms-tab")
        const multiButton = document.getElementById(buttonID)
        if (buttonID === "multi-sample-toggle") {
            multiButton.classList.remove("bi-bookmark-check")
            multiButton.classList.add("bi-bookmark-check-fill", "text-info")
        }
        else if (buttonID === "multi-sample-page-toggle") {
            multiButton.classList.remove("bi-bookmarks")
            multiButton.classList.add("bi-bookmarks-fill", "text-info")
        }
        $(".td-view.view-chromatogram-icon").each(function () {
            $(this).addClass("glow-cell")
        })
        $(".analyte-results-table-sample").each(function () {
            $(this).removeClass("table-primary")
            $(this).find(".bi.bi-eye-fill").removeClass("text-primary")
        })
        if (buttonID === "multi-sample-toggle") {
            $(`#analyte-results-table-sample-id-${this.MULTI_SAMPLE_LIST[0].sampleID}`).addClass("table-primary")
            $(`#analyte-results-table-sample-id-${this.MULTI_SAMPLE_LIST[0].sampleID}`).find(".bi.bi-eye-fill").addClass("text-primary")
            $(`#analyte-results-table-sample-id-${this.MULTI_SAMPLE_LIST[0].sampleID}`).find(".td-view.view-chromatogram-icon").addClass("glow-cell-selected")
        }
        chromatogramTab.classList.remove("justify-content-around")  // remove centering to avoid scroll problems
    }


    unhighlightBookmark(buttonID) {
        console.log("REMOVE HIGHLIGHT BOOKMARK")
        const chromatogramTab = document.getElementById("chromatograms-tab")
        const multiButton = document.getElementById(buttonID)
        multiButton.classList.remove("text-info")
        if (buttonID === "multi-sample-toggle") {
            multiButton.classList.remove("bi-bookmark-check-fill")
            multiButton.classList.add("bi-bookmark-check")
        }
        else if (buttonID === "multi-sample-page-toggle") {
            multiButton.classList.remove("bi-bookmarks-fill")
            multiButton.classList.add("bi-bookmarks")
        }
        $(".analyte-results-table-sample").each(function () {
            $(this).removeClass("table-primary")
            $(this).find(".bi.bi-eye-fill").removeClass("text-primary")
        })
        $(`#analyte-results-table-sample-id-${this.MULTI_SAMPLE_LIST[0]["sampleID"]}`).addClass("table-primary")
        $(`#analyte-results-table-sample-id-${this.MULTI_SAMPLE_LIST[0]["sampleID"]}`).find(".bi.bi-eye-fill").addClass("text-primary")
        $(".td-view.view-chromatogram-icon").each(function () {
            $(this).removeClass("glow-cell")
            $(this).removeClass("glow-cell-selected")
        })
        chromatogramTab.classList.add("justify-content-around")
    }


    toggleMultiSamplePage(event) {
        if (this.MULTI_SAMPLE) {
            this.MULTI_SAMPLE = false;  // Disable MULTI_SAMPLE
            this.unhighlightBookmark("multi-sample-toggle")
        }
        this.MULTI_SAMPLE_PAGE = !this.MULTI_SAMPLE_PAGE
        this.initialMultiSamplePage(event)
        if (this.MULTI_SAMPLE_PAGE) {
            this.highlightBookmark("multi-sample-page-toggle")
        } else {
            this.removeMultiSampleContainers()
            this.MULTI_SAMPLE_LIST = [this.MULTI_SAMPLE_LIST[0]]  // reset multi-sample list to only the first element
            this.unhighlightBookmark("multi-sample-page-toggle")
        }

    }


    initialMultiSamplePage(event) {
        // Selects MULTI_SAMPLE_PAGE number of samples starting from
        // currently selected sample
        if (this.MULTI_SAMPLE_PAGE) {
            this.appendSampleChromatograms(this.CHROM_DATA, true)
            this.channel.perform("get_chromatogram_data_multi_sample_page", {
                "submissionId": this.SUBMISSION_ID,
                "analyteId": this.ANALYTE_ID,
                "sampleId": this.MULTI_SAMPLE_LIST[0]["sampleID"],
                "pageSize": this.MULTI_SAMPLE_PAGE_SIZE,
            })
        }
        else {
            console.log("MULTI_SAMPLE_LIST TOOGLE OFF", this.MULTI_SAMPLE_LIST)
            this.MULTI_SAMPLE_LIST = [this.MULTI_SAMPLE_LIST[0]]  // reset multi-sample list to only the first element
            this.appendSampleChromatograms(this.CHROM_DATA, true)
        }
    }


    updateSample(event) {
        if (this.MULTI_SAMPLE) {
            // console.log("MULTI_SAMPLE", event.currentTarget.dataset)
            this.channel.perform("get_chromatogram_data_multi_sample", {
                "submissionId": event.currentTarget.dataset.submissionid,
                "analyteId": event.currentTarget.dataset.analyteid,
                "sampleId": event.currentTarget.dataset.sampleid,
            })
        } else if (this.MULTI_SAMPLE_PAGE) {
            // Remove previous highlighted eyes and rows
            $(".analyte-results-table-sample").each(function () {
                $(this).removeClass("table-primary")
                $(this).find(".bi.bi-eye-fill").removeClass("text-primary")
            })
            $(".td-view.view-chromatogram-icon").each(function () {
                $(this).removeClass("glow-cell-selected")
            })
            this.channel.perform("get_chromatogram_data_multi_sample_page", {
                "submissionId": event.currentTarget.dataset.submissionid,
                "analyteId": event.currentTarget.dataset.analyteid,
                "sampleId": event.currentTarget.dataset.sampleid,
                "pageSize": this.MULTI_SAMPLE_PAGE_SIZE,
            })
        } else {
            // console.log("DATASET", event.currentTarget.dataset)
            this.channel.perform("get_chromatogram_data", {
                "submissionId": event.currentTarget.dataset.submissionid,
                "analyteId": event.currentTarget.dataset.analyteid,
                "sampleId": event.currentTarget.dataset.sampleid,
            })
        }
    }


    renderCalibrationCurve(data, div, analyteName) {
        console.log("data from renderCalCurve: ", data, div, analyteName)
        // sort cal_points by p['idx'] first
        data.cal_points.sort((a, b) => a.idx - b.idx)
        let options = {
            series: [{
                name: 'Calibration Points',
                type: 'scatter',
                data: data.cal_points
            }, {
                name: 'Best Fit Line',
                type: 'line',
                data: data.best_fit_line
            }],
            chart: {
                height: 450,
                type: 'line',
                events: {
                    markerClick: function (event, chartContext, { seriesIndex, dataPointIndex }) {
                        console.log("MARKER CLICK", seriesIndex, dataPointIndex)
                    }
                },
                zoom: {
                    type: 'xy',
                }
            },
            stroke: {
                colors: ["#F6646E"]
            },
            fill: {
                type: 'solid',
            },
            markers: {
                // size: [20, 0],
                strokeWidth: 1,
                discrete: data.cal_points.map((d, i) => {
                    return {
                        seriesIndex: 0,
                        dataPointIndex: i,
                        fillColor: d.selected ? this.CAL_CURVE_MARKER_COLOR : this.CAL_CURVE_MARKER_COLOR_UNFILLED,
                        size: d.selected ? this.DEFAULT_CAL_CURVE_MARKER_SIZE : this.DEFAULT_CAL_CURVE_MARKER_SIZE / 2,
                        fillOpacity: 1,
                    }
                })
            },
            tooltip: {
                enabled: false,
                // shared: false,
                // intersect: true,
                // custom: function({series, seriesIndex, dataPointIndex, w}) {
                //     return `<div class="card border-0">
                //                 <div class="card-header bg-dark" style="color: white;">
                //                     <span style='font-size: 1.2rem; font-weight: bold; margin: 0; padding: 0'>Point ${dataPointIndex+1}</span>
                //                 </div>
                //                 <div class="p-2 card" style="border-radius: 0px 0px 5px 5px;">
                //                     <span>Peak Area Ratio: ${series[seriesIndex][dataPointIndex].toFixed(2)}</span>
                //                     <span>Calibration Concentration (µM): ${data.cal_points[dataPointIndex].x}</span>
                //                 </div>
                //             </div>`
                // }
            },
            legend: {
                show: false
            },
            yaxis: {
                title: {
                    text: Object.values(this.HAS_NO_ISTDS).includes(analyteName) ? 'Analyte Peak Area' : 'Peak Area Ratio'
                },
                labels: {
                    formatter: function (value) {
                        return value.toFixed(2)
                    }
                },
            },
            xaxis: {
                type: 'numeric',
                title: {
                    text: 'Calibration Concentration (µM)'
                },
                min: 0
            },
            title: {
                text: `${analyteName} Calibration Curve`,
                align: 'left',
            },
            noData: {
                text: 'No calibration curve data available for this analyte.',
            },
            states: {
                active: {
                    allowMultipleDataPointsSelection: true,
                    filter: {
                        type: 'none'
                    }
                }
            }
        };

        let chart = new ApexCharts(div, options);
        chart.render();



        return chart;

    }

    appendSampleChromatograms(data, clear = false) {
        let container = this.chromatogramPanelTarget
        let graphContainerClone = container.childNodes[0].cloneNode(true)
        console.log("GRAPH CONTAINER CLONE", graphContainerClone)
        // console.log(container.childNodes)
        graphContainerClone.id = `paired-container-sample-${data["sample_id"]}`
        if (clear) {
            this.removeAllMultiSampleContainers()
        }
        container.appendChild(graphContainerClone)
        graphContainerClone.querySelector("h6").textContent = data["sample_filename"].replace('.mzML', '')
        $(graphContainerClone).find(".analyte-feedback").find(".og-rt").text(data.analyte.rmin === data.analyte.rmax ? "No Peak(s) Detected" : `${data.analyte.rmin != null ? parseFloat(data.analyte.rmin).toFixed(2) : ''} to ${data.analyte.rmax != null ? parseFloat(data.analyte.rmax).toFixed(2) : ''} minutes [${data.analyte.apexrt != null ? parseFloat(data.analyte.apexrt).toFixed(2) : ''}]`)
        $(graphContainerClone).find(".istd-feedback").find(".og-rt").text(data.istd.rmin === data.istd.rmax ? "No Peak(s) Detected" : `${data.istd.rmin != null ? parseFloat(data.istd.rmin).toFixed(2) : ''} to ${data.istd.rmax != null ? parseFloat(data.istd.rmax).toFixed(2) : ''} minutes [${data.istd.apexrt != null ? parseFloat(data.istd.apexrt).toFixed(2) : ''}]`)

        let pairedContainerClone = graphContainerClone.querySelector(".paired-container")
        let analyteSVG = pairedContainerClone.querySelectorAll(".sample-graph")[0]
        this.renderChromatogram(data["analyte"], analyteSVG, "analyte", data["area_to_use"], this.isFia(), data["sample_id"], data["sample_filename"], (this.MULTI_SAMPLE || this.MULTI_SAMPLE_PAGE) ? this.OG_HEIGHT * this.HEIGHT_MULTIPLIER : this.OG_HEIGHT, clear ? 0 : this.MULTI_SAMPLE_LIST.length)
        let istdSVG = pairedContainerClone.querySelectorAll(".sample-graph")[1]
        this.renderChromatogram(data["istd"], istdSVG, "istd", data["area_to_use"], this.isFia(), data["sample_id"], data["sample_filename"], (this.MULTI_SAMPLE || this.MULTI_SAMPLE_PAGE) ? this.OG_HEIGHT * this.HEIGHT_MULTIPLIER : this.OG_HEIGHT, clear ? 0 : this.MULTI_SAMPLE_LIST.length)
        this.MULTI_SAMPLE_LIST.push({
            "sampleID": data["sample_id"],
            "sampleFilename": data["sample_filename"],
            "analyte": { "chart": analyteSVG, "data": data["analyte"], "newRmin": -1, "newRmax": -1 },
            "istd": { "chart": istdSVG, "data": data["istd"], "newRmin": -1, "newRmax": -1 },
        })
        console.log("MULTI_SAMPLE_LIST", this.MULTI_SAMPLE_LIST)

    }

    // from Submission.jsx
    renderChromatogram(data, targetElement, compoundType, areaToUse, IS_FIA, selectedSample, sampleFilename, graphHeight, sampleSerial) {
        console.log("targetElement from renderChromatogram>>>>>:", this.MULTI_SAMPLE_LIST, this.ANALYTE_NAME)
        if (Object.values(this.HAS_NO_ISTDS).includes(this.ANALYTE_NAME) && compoundType === "istd") {
            // replace targetElement SVG element with a message indicating that the internal standard is not applicable for this analyte
            console.log("RENDERING NO ISTD MESSAGE", targetElement)
            targetElement.innerHTML = `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="16" fill="#999">This analyte uses external calibration.</text>`
            return
        }



        let sampleData, sampleIdx, rminIdx, rmaxIdx, rtValues;
        // console.log("TITLES: ", this.EXP_CHROM_TITLE, this.ISTD_CHROM_TITLE, title)
        if (compoundType === "analyte") {
            // console.log("rendering exp chromatogram")
            sampleData = data
            rminIdx = this.findClosestIndex(sampleData.values.map(d => d.x), sampleData.rmin)
            rmaxIdx = this.findClosestIndex(sampleData.values.map(d => d.x), sampleData.rmax)

        } else if (compoundType === "istd") {
            // console.log("rendering istd chromatogram")
            sampleData = data
            rminIdx = this.findClosestIndex(sampleData.values.map(d => d.x), sampleData.rmin)
            rmaxIdx = this.findClosestIndex(sampleData.values.map(d => d.x), sampleData.rmax)
        }
        rtValues = sampleData.values.slice(rminIdx, rmaxIdx + 1)
        // console.log(">> rminIdx, rmin", rminIdx, sampleData.values[rminIdx], sampleData.rmin)
        // console.log("rmaxIdx, rmax", rmaxIdx, sampleData.values[rmaxIdx], sampleData.rmax)
        // console.log("data from renderChromatogram: ", data)
        let colors = {
            exp: {
                fill: ['rgba(246, 100, 110, 0.4)'],
                stroke: ['#F6646E'],
            },
            istd: {
                fill: ['rgba(69, 150, 182, 0.4)'],
                stroke: ['#4596B6'],
            },
        }
        let fillColors = ["transparent", "#ffffff", compoundType === "analyte" ? colors.exp.fill[0] : colors.istd.fill[0]]
        sampleFilename = sampleFilename.split('_').slice(5, -1).join('_')
        if (rminIdx > -1 && rmaxIdx > -1) {  // this means that the retention time window has been set
            let zoomedData = sampleData.values
            zoomedData = zoomedData.sort((a, b) => a.x - b.x).filter((d) => sampleData.values[rminIdx]['x'] - d.x < this.CHROMATOGRAM_RETENTION_TIME_WINDOW_BUFFER && d.x - sampleData.values[rmaxIdx]['x'] < this.CHROMATOGRAM_RETENTION_TIME_WINDOW_BUFFER)
        } else if (rminIdx === rmaxIdx) {  // this means that the retention time window has not been set by backend (due to failure of peak picking), is usually just the first point
        } else {  // if any othe errors occur, just plot the entire chromatogram
        }
        let xValues = sampleData.values.map(d => d['x']);
        let yValues = sampleData.values.map(d => d['y']);

        // console.log("AREATOUSE", areaToUse)
        // console.log("AREATOUSE sampleData.values[rminIdx], sampleData.values[rmaxIdx]", sampleData.values[rminIdx], sampleData.values[rmaxIdx])
        // console.log("AREATOUSE rminIdx, rmaxIdx", rminIdx, rmaxIdx)

        // Find the maximum and minimum values of 'x'
        let maxX = Math.max(...xValues) + 0.005 * Math.max(...xValues);
        let minX = Math.min(...xValues) - 0.005 * Math.max(...xValues);
        let maxY = Math.max(...yValues) + 0.025 * Math.max(...yValues);
        let maxRTY = -Infinity; // Initialize maxRTY to a very small number
        let maxRTX = null; // Initialize maxRTX to null
        let xRTExtent = d3.extent(rtValues, d => d.x)
        let yRTExtent = d3.extent(rtValues, d => d.y)

        // Iterate through the list of dictionaries
        rtValues.forEach(dict => {
            // Check if the 'y' value of the current dictionary is greater than the current maxRTY
            if (dict.y > maxRTY) {
                // If it is, update maxRTY and maxRTX
                maxRTY = dict.y;
                maxRTX = dict.x;
            }
        });
        console.log("MAX RT Y AND X", maxRTY, maxRTX)
        let minY = 0;
        // Default to rmin and rmax
        let newrminIdx = rminIdx;
        let newrmaxIdx = rmaxIdx;
        console.log("MIN", newrminIdx)
        console.log("MAX", newrmaxIdx)
        // console.log("Max X: ", maxX)
        // console.log("Min X: ", minX)
        // console.log("Max Y: ", maxY)
        // Declare the chart dimensions and margins.
        const width = 1248;
        const height = graphHeight
        const padding = { top: 50, bottom: 70, left: 90, right: 30 }
        // Calculate the slope (m) and intercept (b) of the line connecting the two points
        let m, b;
        if (sampleData.values.length > 0) {
            m = (sampleData.values[rmaxIdx].y - sampleData.values[rminIdx].y) / (sampleData.values[rmaxIdx].x - sampleData.values[rminIdx].x);
            b = sampleData.values[rminIdx].y - m * sampleData.values[rminIdx].x;
        }
        // Declare the x (horizontal position) scale. 
        const xRT = [xRTExtent[0] - this.CHROMATOGRAM_RETENTION_TIME_WINDOW_BUFFER, xRTExtent[1] + this.CHROMATOGRAM_RETENTION_TIME_WINDOW_BUFFER]
        const x = d3.scaleLinear(rminIdx == rmaxIdx ? [minX, maxX] : xRT, [0, width]);
        let x2 = x.copy();

        // Calculate Y max including deconvolved peak heights if available
        let yMaxForScale = rminIdx == rmaxIdx ? maxY : yRTExtent[1] * 1.025;
        const hasDeconvolutionData = data.deconvolved?.has_deconvolution;
        if (hasDeconvolutionData) {
            const currentPeakValues = data.deconvolved.current_peak?.values || [];
            const otherPeakValues = data.deconvolved.other_peak?.values || [];
            const deconvMaxY = Math.max(
                ...currentPeakValues.map(v => v.y),
                ...otherPeakValues.map(v => v.y),
                0
            );
            yMaxForScale = Math.max(yMaxForScale, deconvMaxY * 1.1); // Add 10% headroom
        }

        // Declare the y (vertical position) scale.
        const y = d3.scaleLinear([0, yMaxForScale], [height, 0]);
        let y2 = y.copy();
        let selectionExtent = [-1, -1];

        // Declare the line generator.
        const line = d3.line()
            .x(d => x2(d.x))
            .y(d => y2(d.y));

        // Declare the area generator.
        // Area spans to y == 0 when area to integrate is vertical or is FIA panel
        const area = d3.area()
            .x(d => x2(d.x))
            .y0(d => {
                return (areaToUse == "area_vertical_raw" || IS_FIA) ? y2(0) : y2(Math.min(d.y, m * d.x + b))
            })
            .y1(d => y2(d.y));

        const whiteArea = d3.area()
            .x(d => x2(d.x))
            .y0(y2(0))
            .y1(d => {
                return (areaToUse == "area_vertical_raw" || IS_FIA) ? y2(0) : y2(m * d.x + b)
            })

        // Create the SVG container.
        const svg = d3.select(targetElement)
            .attr("width", "100%")
            .attr("height", height)
            .attr("viewBox", [0, 0, width + padding.right + padding.left, height + padding.top + padding.bottom])
            .attr("style", "max-width: 100%; height: auto;")


        svg.selectAll("*").remove();

        // Cross-browser fix: neutralize CSS zoom on SVG so D3 coordinates work.
        //
        // body { zoom: 0.8 } causes getScreenCTM() and event.clientX to use
        // mismatched coordinate systems in Firefox/Safari (Chrome is fine).
        // D3's brush relies on getScreenCTM().inverse() to map mouse positions
        // to SVG coordinates, so the mismatch shifts selections to wrong RT.
        //
        // Fix: apply counter-zoom on the SVG so its effective zoom = 1.0.
        // With no net zoom on the SVG, coordinate math is trivially correct
        // in all browsers. Compensate CSS width so visual size is unchanged.
        const cssZoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
        if (cssZoom !== 1) {
            const svgEl = svg.node();
            const parentRect = svgEl.parentElement.getBoundingClientRect();
            const svgRect = svgEl.getBoundingClientRect();
            const widthFraction = svgRect.width / parentRect.width;

            // Counter-zoom: body(0.8) * svg(1.25) = 1.0 effective zoom
            svgEl.style.zoom = `${1 / cssZoom}`;
            // Shrink CSS width to compensate: layout_width * counter_zoom = original
            // e.g. 40% * 1.25 = 50% layout contribution, matching original .w-50
            const adjustedPct = widthFraction * cssZoom * 100;
            svgEl.style.setProperty('width', `${adjustedPct}%`, 'important');
        }

        const plotArea = svg.append("g")
            .attr("transform", "translate(" + [padding.left, padding.top] + ")")

        const clippingRect = plotArea
            .append("clipPath")
            .attr("id", "clippy")
            .append("rect")
            .attr("width", width)
            .attr("height", height)
            .attr("fill", "none")

        // Determine if we should show deconvolution view
        const integrationType = data.integration_type;
        const isExplicitlyPeakPicking = integrationType === 'peak_picking';
        const showDeconvolution = hasDeconvolutionData && !isExplicitlyPeakPicking;

        console.log("[RENDER] hasDeconvolutionData:", hasDeconvolutionData, "integrationType:", integrationType, "isExplicitlyPeakPicking:", isExplicitlyPeakPicking, "showDeconvolution:", showDeconvolution);

        const areaPath = plotArea.append("path")
            .attr("fill", compoundType === "analyte" ? colors.exp.fill[0] : colors.istd.fill[0])
            .attr("d", area(rtValues))
            .attr("clip-path", "url(#clippy)")
            .attr("opacity", showDeconvolution ? 0 : 1); // Hide when showing deconvolution

        // Append a path for the area (under the axes) - HIDE when showing deconvolution
        const whiteAreaPath = plotArea.append("path")
            .attr("fill", "white")
            .attr("d", whiteArea(rtValues))
            .attr("clip-path", "url(#clippy)")
            .attr("opacity", showDeconvolution ? 0 : 1); // Hide when showing deconvolution

        // Append a path for the line (raw chromatogram - light trace when deconvolution shown)
        const linePath = plotArea.append("path")
            .attr("stroke", compoundType === "analyte" ? colors.exp.stroke[0] : colors.istd.stroke[0])
            .attr("stroke-width", 3) // Set the stroke width
            .attr("fill", "none")
            .attr("d", line(sampleData.values))
            .attr("clip-path", "url(#clippy)")
            .attr("opacity", showDeconvolution ? 0.3 : 1); // Light trace if showing deconvolution

        // Create tooltip for data points (used by both regular and deconvolved points)
        var Tooltip = d3.select(targetElement.parentElement)
            .append("div")
            .style("opacity", 0)
            .attr("class", "d3-chart-tooltip shadow")
            .style("position", "fixed")
            .style("background-color", "rgba(255, 243, 205, 0.75)")
            .style("border", "dashed")
            .style("border-width", "1px")
            .style("border-radius", "5px")
            .style("padding", "5px")
            .style("pointer-events", "none")
            .style("z-index", "9999");

        // Mouse event handlers for tooltip
        var mouseover = function (event, d) {
            Tooltip.style("opacity", 1);
        };
        var mousemove = function (event, d) {
            // CSS zoom: 0.8 on body means fixed-position CSS values are scaled.
            // Divide clientX/Y by zoom so the tooltip lands at the cursor.
            const zoomLevel = parseFloat(getComputedStyle(document.body).zoom) || 1;
            Tooltip
                .html(`<div class="fs-5"><b>Intensity:</b> ${d.y.toExponential(2)}<br> <b>Time:</b> ${d.x.toFixed(2)} minutes</div>`)
                .style("left", (event.clientX / zoomLevel + 5) + "px")
                .style("top", (event.clientY / zoomLevel - 60) + "px");
        };
        var mouseleave = function (event, d) {
            Tooltip.style("opacity", 0);
        };

        // Deconvolution overlay - draw deconvolved peaks if available AND in deconvolution mode
        let deconvCurrentLinePath = null;
        let deconvOtherLinePath = null;
        let deconvCurrentAreaPath = null;
        let deconvOtherAreaPath = null;
        let deconvCurrentDataPoints = null;
        let deconvOtherDataPoints = null;

        // Colors for deconvolved peaks - Red for analyte, Green for ISTD, grey for partner
        const currentPeakFillColor = compoundType === "analyte" ? '#F6646E' : '#4596B6';
        const otherPeakFillColor = '#888888';

        if (showDeconvolution) {
            const currentPeak = data.deconvolved.current_peak;
            const otherPeak = data.deconvolved.other_peak;

            // Debug: log deconvolved data
            console.log("[DECONVOLUTION] Current peak data:", currentPeak);
            console.log("[DECONVOLUTION] Other peak data:", otherPeak);
            console.log("[DECONVOLUTION] Current peak values count:", currentPeak?.values?.length);
            console.log("[DECONVOLUTION] Other peak values count:", otherPeak?.values?.length);

            // Only draw if we have enough data points (at least 10 for a meaningful curve)
            const minPointsRequired = 10;

            // Draw OTHER peak FIRST (so it's behind the current peak)
            if (otherPeak?.values && otherPeak.values.length >= minPointsRequired) {
                // Sort values by x (RT) to ensure proper line drawing
                const otherValues = [...otherPeak.values].sort((a, b) => a.x - b.x);

                // Area fill under other peak (grey)
                const otherArea = d3.area()
                    .x(d => x2(d.x))
                    .y0(y2(0))
                    .y1(d => y2(d.y));

                deconvOtherAreaPath = plotArea.append("path")
                    .attr("fill", otherPeakFillColor)
                    .attr("fill-opacity", 0.4)
                    .attr("d", otherArea(otherValues))
                    .attr("clip-path", "url(#clippy)");

                // Line for other peak
                const otherLine = d3.line()
                    .x(d => x2(d.x))
                    .y(d => y2(d.y));

                deconvOtherLinePath = plotArea.append("path")
                    .attr("stroke", otherPeakFillColor)
                    .attr("stroke-width", 1)
                    .attr("fill", "none")
                    .attr("d", otherLine(otherValues))
                    .attr("clip-path", "url(#clippy)");;

                // RT label removed for other peak (grey shading)
            } else if (otherPeak?.values) {
                console.warn("[DECONVOLUTION] Other peak has insufficient data points:", otherPeak.values.length);
            }

            // Draw CURRENT peak (on top)
            if (currentPeak?.values && currentPeak.values.length >= minPointsRequired) {
                // Sort values by x (RT) to ensure proper line drawing
                const currentValues = [...currentPeak.values].sort((a, b) => a.x - b.x);

                // Area fill under current deconvolved peak (red/purple)
                const currentArea = d3.area()
                    .x(d => x2(d.x))
                    .y0(y2(0))
                    .y1(d => y2(d.y));

                deconvCurrentAreaPath = plotArea.append("path")
                    .attr("fill", currentPeakFillColor)
                    .attr("fill-opacity", 0.4)
                    .attr("d", currentArea(currentValues))
                    .attr("clip-path", "url(#clippy)");

                // Line for current peak
                const currentLine = d3.line()
                    .x(d => x2(d.x))
                    .y(d => y2(d.y));

                deconvCurrentLinePath = plotArea.append("path")
                    .attr("stroke", currentPeakFillColor)
                    .attr("stroke-width", 3)
                    .attr("fill", "none")
                    .attr("d", currentLine(currentValues))
                    .attr("clip-path", "url(#clippy)");

                // RT label removed for current peak (red shading)
            } else if (currentPeak?.values) {
                console.warn("[DECONVOLUTION] Current peak has insufficient data points:", currentPeak.values.length);
            }

            // Add legend in top-right corner - using FILL colors (rectangles)
            const legendGroup = plotArea.append("g")
                .attr("transform", `translate(${width - 250}, 10)`);

            // Legend background - much bigger for larger text
            legendGroup.append("rect")
                .attr("x", -12)
                .attr("y", -10)
                .attr("width", 250)
                .attr("height", otherPeak ? 85 : 45)
                .attr("fill", "white")
                .attr("fill-opacity", 0.95)
                .attr("stroke", "#ccc")
                .attr("stroke-width", 2)
                .attr("rx", 5);

            // Current peak legend entry - filled rectangle (much bigger)
            legendGroup.append("rect")
                .attr("x", 0)
                .attr("y", 5)
                .attr("width", 35)
                .attr("height", 22)
                .attr("fill", currentPeakFillColor)
                .attr("fill-opacity", 0.7)
                .attr("stroke", currentPeakFillColor)
                .attr("stroke-width", 1);

            legendGroup.append("text")
                .attr("x", 45)
                .attr("y", 22)
                .attr("fill", "#333")
                .attr("font-size", "18px")
                .attr("font-weight", "600")
                .text(currentPeak?.analyte_name || "Current");

            // Other peak legend entry (if exists) - filled rectangle (much bigger)
            if (otherPeak) {
                legendGroup.append("rect")
                    .attr("x", 0)
                    .attr("y", 40)
                    .attr("width", 35)
                    .attr("height", 22)
                    .attr("fill", otherPeakFillColor)
                    .attr("fill-opacity", 0.7)
                    .attr("stroke", otherPeakFillColor)
                    .attr("stroke-width", 1);

                legendGroup.append("text")
                    .attr("x", 45)
                    .attr("y", 57)
                    .attr("fill", "#333")
                    .attr("font-size", "18px")
                    .attr("font-weight", "600")
                    .text(otherPeak?.analyte_name || (compoundType === "analyte" ? "ISTD" : "Analyte"));
            }

            // Show warning if data is sparse (needs re-processing)
            const currentHasEnoughData = currentPeak?.values?.length >= minPointsRequired;
            const otherHasEnoughData = !otherPeak || otherPeak?.values?.length >= minPointsRequired;

            if (!currentHasEnoughData || !otherHasEnoughData) {
                const sparseCount = currentPeak?.values?.length || 0;
                console.error(`[DECONVOLUTION] Data is too sparse (${sparseCount} points). Re-process submission to regenerate data.`);

                // Add warning text on the chart
                plotArea.append("text")
                    .attr("x", width / 2)
                    .attr("y", 50)
                    .attr("text-anchor", "middle")
                    .attr("fill", "#d32f2f")
                    .attr("font-size", "16px")
                    .attr("font-weight", "bold")
                    .text("⚠️ Incomplete deconvolution data - please re-process submission");
            }

        }

        // Brushing
        let controller = this
        let newRmin, newRmax;
        const brush = d3.brushX()
            .extent([[0, 0], [width, height]])
            .on('brush end', function (event) {
                const selection = event.selection;
                let value = [];
                // Check if there's a selection
                if (selection) {
                    // Convert pixel coordinates to data values
                    const x0 = x2.invert(selection[0]);
                    const x1 = x2.invert(selection[1]);
                    console.log("VALUES", x0, x1)

                    // For deconvolved chromatograms, use the deconvolved current peak data points
                    // For regular chromatograms, use the original data points
                    if (showDeconvolution && deconvCurrentDataPoints) {
                        // Reset all deconvolved points to yellow
                        deconvCurrentDataPoints.style("fill", "yellow");
                        if (deconvOtherDataPoints) {
                            deconvOtherDataPoints.style("fill", otherPeakFillColor);
                        }

                        // Filter and highlight selected points (current peak only for reintegration)
                        value = deconvCurrentDataPoints
                            .filter((d) => x0 <= d.x && d.x <= x1)
                            .style("fill", "blue")
                            .data();
                        console.log("[DECONVOLUTION] Selected deconvolved points:", value.length);
                    } else {
                        value = dataPoints
                            .style("fill", "yellow")
                            .filter((d) => x0 <= d.x && d.x <= x1)
                            .style("fill", "blue")
                            .data();
                    }
                    console.log("value", value)
                    if (value.length != 0) {
                        newRmin = value[0].x
                        newRmax = value[value.length - 1].x
                        // For deconvolved data, find index in the deconvolved values array
                        if (showDeconvolution && data.deconvolved?.current_peak?.values) {
                            const deconvValues = [...data.deconvolved.current_peak.values].sort((a, b) => a.x - b.x);
                            newrminIdx = deconvValues.findIndex(d => d.x === value[0].x);
                            newrmaxIdx = deconvValues.findIndex(d => d.x === value[value.length - 1].x);
                        } else {
                            newrminIdx = sampleData.values.findIndex(d => d.x === value[0].x)
                            newrmaxIdx = sampleData.values.findIndex(d => d.x === value[value.length - 1].x)
                        }
                        // console.log(newrminIdx, newrmaxIdx)
                        console.log("SERIAL", sampleSerial)
                        controller.MULTI_SAMPLE_LIST[sampleSerial][compoundType]["newRmin"] = newRmin
                        controller.MULTI_SAMPLE_LIST[sampleSerial][compoundType]["newRmax"] = newRmax
                        console.log("MULTI_SAMPLE_LIST", controller.MULTI_SAMPLE_LIST, newRmin, newRmax)
                        selectionExtent = [x0, x1];
                    }

                    // update the feedback text
                    $(targetElement).parent().parent().find(`.${compoundType}-feedback`).find(".new-rt").text(`${newRmin ? parseFloat(newRmin).toFixed(2) : ''}, ${newRmax ? parseFloat(newRmax).toFixed(2) : ''}`)

                }
                else {
                    selectionExtent = [-1, -1];
                    // Reset point colors when selection is cleared
                    if (showDeconvolution && deconvCurrentDataPoints) {
                        deconvCurrentDataPoints.style("fill", "yellow");
                        if (deconvOtherDataPoints) {
                            deconvOtherDataPoints.style("fill", otherPeakFillColor);
                        }
                    } else {
                        dataPoints.style("fill", "yellow");
                    }
                    this.newrminIdx = this.rminIdx
                    this.newrmaxIdx = this.rmaxIdx
                }
                // Inform downstream cells that the selection has changed.
                svg.property("value", value).dispatch("input");

            });

        plotArea.call(brush)

        // Plot data points - HIDE when deconvolution is shown to reduce clutter
        // When deconvolved, RT annotations already provide key information
        let pointsData = showDeconvolution ? [] : sampleData.values;

        const dataPoints = plotArea.append("g")
            .selectAll("circle")
            .data(pointsData)
            .join("circle")
            .attr("cx", d => x2(d.x))
            .attr("cy", d => y2(d.y))
            .attr("r", 6) // Adjust the radius as needed
            .attr("fill", "yellow")
            .attr("stroke", "black")
            .attr("stroke-width", 1)
            .attr("clip-path", "url(#clippy)")
            .style('pointer-events', 'all')
            .on("mouseover", mouseover)
            .on("mousemove", mousemove)
            .on("mouseleave", mouseleave)

        // Draw deconvolved data points AFTER brush so they can receive mouse events
        // Filter function to display points every 0.01 minutes for cleaner visualization
        const filterPointsByInterval = (values, interval = 0.01) => {
            if (!values || values.length === 0) return [];
            const sorted = [...values].sort((a, b) => a.x - b.x);
            const filtered = [];
            let lastX = -Infinity;
            for (const point of sorted) {
                // Include point if it's at least 'interval' away from last included point
                if (point.x - lastX >= interval - 0.0001) { // Small tolerance for floating point
                    filtered.push(point);
                    lastX = point.x;
                }
            }
            return filtered;
        };

        if (showDeconvolution) {
            const currentPeak = data.deconvolved.current_peak;
            const otherPeak = data.deconvolved.other_peak;
            const minPointsRequired = 10;

            // Draw data points for OTHER peak - colored circles for reintegration selection
            // Display every 0.01 minute for cleaner visualization
            if (otherPeak?.values && otherPeak.values.length >= minPointsRequired) {
                const otherValuesFiltered = filterPointsByInterval(otherPeak.values, 0.01);
                deconvOtherDataPoints = plotArea.append("g")
                    .attr("class", "deconv-other-points")
                    .selectAll("circle")
                    .data(otherValuesFiltered)
                    .join("circle")
                    .attr("cx", d => x2(d.x))
                    .attr("cy", d => y2(d.y))
                    .attr("r", 5)
                    .attr("fill", otherPeakFillColor)
                    .attr("stroke", otherPeakFillColor)
                    .attr("stroke-width", 1)
                    .attr("clip-path", "url(#clippy)")
                    .style('pointer-events', 'all')
                    .on("mouseover", mouseover)
                    .on("mousemove", mousemove)
                    .on("mouseleave", mouseleave);
            }

            // Draw data points for CURRENT peak - colored circles for reintegration selection
            // Display every 0.01 minute for cleaner visualization
            if (currentPeak?.values && currentPeak.values.length >= minPointsRequired) {
                const currentValuesFiltered = filterPointsByInterval(currentPeak.values, 0.01);
                deconvCurrentDataPoints = plotArea.append("g")
                    .attr("class", "deconv-current-points")
                    .selectAll("circle")
                    .data(currentValuesFiltered)
                    .join("circle")
                    .attr("cx", d => x2(d.x))
                    .attr("cy", d => y2(d.y))
                    .attr("r", 6)
                    .attr("fill", "yellow")
                    .attr("stroke", "black")
                    .attr("stroke-width", 1)
                    .attr("clip-path", "url(#clippy)")
                    .style('pointer-events', 'all')
                    .on("mouseover", mouseover)
                    .on("mousemove", mousemove)
                    .on("mouseleave", mouseleave);
            }
        }

        // function that sets the tooltip's HTML when revealed
        // can be passed the hovered selection's datum
        function tooltipContents(d) {
            return `Time (mins): ${d.x}, Intensity: ${d.y}`;
        }

        // Add the x-axis.
        const xAxis = d3.axisBottom(x2);
        const gx = plotArea.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(xAxis.ticks(10).tickSizeOuter(0))
            .call(g => g.append("text")
                .attr("x", width / 2)
                .attr("y", padding.bottom / 1.2)
                .attr("fill", "currentColor")
                .attr("text-anchor", "middle")
                .text("Retention Time (mins)"))
            .style("font-size", "min(3em, 24px)");
        // console.log("width / 2: ", width / 2)

        // Add the y-axis, remove the domain line, add grid lines and a label.
        const yAxis = d3.axisLeft(y2);
        const gy = plotArea.append("g")
            .attr("transform", `translate(${padding.left / 25}, 0)`)
            // .call(yAxis.tickValues(y2.ticks(this.MULTI_SAMPLE ? 2 : 5)).tickFormat(d3.format(".1e")))
            .call(yAxis.ticks(this.MULTI_SAMPLE | this.MULTI_SAMPLE_PAGE ? 2 : 5).tickFormat(d3.format(".1e")))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
                .attr("x2", width)
                .attr("stroke-opacity", 0.1))
            .style("font-size", "min(3em, 24px)")
            .call(g => g.append("text")
                .attr("x", 0)
                .attr("y", -padding.top / 4)
                .attr("fill", "currentColor")
                .attr("text-anchor", "start")
                .text(`${this.getCompoundTypeString(compoundType)} Intensity`))

        // Check if data is available
        if (sampleData.values.length === 0) {
            // No data available, display a message or placeholder
            svg.append("text")
                .attr("transform", "translate(" + [width / 2 - padding.right, height / 2] + ")")
                .attr("fill", "currentColor")
                .text("No Data Found")
                .style("font-size", "min(5em, 32px)");
        }


        // const brushArea = svg.append('g')
        // .attr("transform", "translate("+[padding.left, padding.top]+")")




        function zoomed(event) {
            const t = event.transform;
            if (t.k <= 0.95) {
                x.domain([minX, maxX])
                y.domain([0, maxY])
            }
            x2 = t.rescaleX(x);
            y2 = t.rescaleY(y);

            // Redraw scales
            gx.call(xAxis.scale(x2))
            gy.call(yAxis.scale(y2))
                .call(g => g.select(".domain").remove())
            gy.selectAll(".tick line")
                .attr("x2", width)
                .attr("stroke-opacity", 0.1);

            // Redraw paths
            linePath.attr("d", line(sampleData.values))
            areaPath.attr("d", area(rtValues, x2, y2))
            whiteAreaPath.attr("d", whiteArea(rtValues, x2, y2))
            dataPoints.attr("cx", d => x2(d.x)).attr("cy", d => y2(d.y))

            // Redraw deconvolved paths if they exist
            if (deconvCurrentLinePath && data.deconvolved?.current_peak?.values) {
                const currentValues = [...data.deconvolved.current_peak.values].sort((a, b) => a.x - b.x);
                const currentLine = d3.line().x(d => x2(d.x)).y(d => y2(d.y));
                deconvCurrentLinePath.attr("d", currentLine(currentValues));
            }
            if (deconvOtherLinePath && data.deconvolved?.other_peak?.values) {
                const otherValues = [...data.deconvolved.other_peak.values].sort((a, b) => a.x - b.x);
                const otherLine = d3.line().x(d => x2(d.x)).y(d => y2(d.y));
                deconvOtherLinePath.attr("d", otherLine(otherValues));
            }
            if (deconvCurrentAreaPath && data.deconvolved?.current_peak?.values) {
                const currentValues = [...data.deconvolved.current_peak.values].sort((a, b) => a.x - b.x);
                const currentArea = d3.area().x(d => x2(d.x)).y0(y2(0)).y1(d => y2(d.y));
                deconvCurrentAreaPath.attr("d", currentArea(currentValues));
            }
            // CRITICAL FIX: Also redraw the OTHER peak area path during zoom
            if (deconvOtherAreaPath && data.deconvolved?.other_peak?.values) {
                const otherValues = [...data.deconvolved.other_peak.values].sort((a, b) => a.x - b.x);
                const otherArea = d3.area().x(d => x2(d.x)).y0(y2(0)).y1(d => y2(d.y));
                deconvOtherAreaPath.attr("d", otherArea(otherValues));
            }

            // Redraw deconvolved data points if they exist
            if (deconvCurrentDataPoints) {
                deconvCurrentDataPoints.attr("cx", d => x2(d.x)).attr("cy", d => y2(d.y));
            }
            if (deconvOtherDataPoints) {
                deconvOtherDataPoints.attr("cx", d => x2(d.x)).attr("cy", d => y2(d.y));
            }
            // Redraw selection
            if (selectionExtent[0] !== -1 && selectionExtent[1] !== -1) {
                // get the brush's x0/x1
                const [x0, x1] = selectionExtent.map(x);
                // rescale the coordinates to the new scale
                const _x0 = x0 * t.k + t.x;
                const _x1 = x1 * t.k + t.x;

                // move the brush
                brush.move(plotArea, [_x0, _x1]); // update brush extent
            }

        }

        const zoom = d3.zoom()
            .scaleExtent([0.95, 64])
            .extent([[-padding.left, -padding.top], [width + padding.right, height]])
            .translateExtent([
                [-padding.left, -padding.top],
                [width + padding.right, height]
            ])
            .on("zoom", zoomed)
            .wheelDelta(customWheelDelta)
            .filter(function (event) {
                return (event.button === 0 && event.ctrlKey)  // || event.type === "wheel";
            })

        function customWheelDelta(event) {
            return -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002);
        }
        // const zoomArea = plotArea.append('rect')
        //     .attr('width', width)
        //     .attr('height', height)
        //     .style('fill', 'none')
        //     .style('pointer-events', 'all');

        // // Disables context on right click 
        // svg.on("contextmenu", function (event) {
        //     console.log("RIGHT CLICK")
        //     // Prevent the default context menu
        //     event.preventDefault();
        // });

        svg.call(zoom)
            .on("dblclick.zoom", function () {
                // Replace double click zoom to reset zoom
                svg.call(zoom.transform, d3.zoomIdentity)
            })
        console.log("IDENTITY", d3.zoomIdentity)
        svg.call(zoom.transform, d3.zoomIdentity)

        // Set up the initial zoom transformation to focus on the portion of points
        // let scale = maxY / maxRTY;
        // let initialTransform = d3.zoomIdentity
        //     .translate(-x(xRTExtent[0]) - padding.left, -y(yRTExtent[0]) - padding.top) // Translate to the top-left corner of the portion
        //     .scale(1 / Math.max((x(xRTExtent[1]) - x(xRTExtent[0])) / width, (y(yRTExtent[1]) - y(yRTExtent[0])) / height)); // Scale to fit the portion within the viewport

        // Apply the initial zoom transformation to the SVG element
        // svg.call(zoom.transform, initialTransform);

        return svg.node();
    }

    updateGraphHeight(ogH, mult) {
        let analyte = this.MULTI_SAMPLE_LIST[0]["analyte"]
        let istd = this.MULTI_SAMPLE_LIST[0]["istd"]
        let newHeight = ogH * mult
        if (this.MULTI_SAMPLE) {
            d3.select(analyte["chart"])
                .attr("preserveAspectRatio", "none")
                .attr("height", `${newHeight}px`);
            d3.select(istd["chart"])
                .attr("preserveAspectRatio", "none")
                .attr("height", `${newHeight}px`);
        } else {
            d3.select(analyte["chart"])
                .attr("preserveAspectRatio", "none")
                .attr("height", `${ogH}px`);
            d3.select(istd["chart"])
                .attr("preserveAspectRatio", "none")
                .attr("height", `${ogH}px`);
        }
    }

    findClosestIndex(array, target) {
        if (array.length === 0) return -1
        let closestIdx = 0
        let closestDiff = Math.abs(target - array[0])
        for (let i = 1; i < array.length; i++) {
            let diff = Math.abs(target - array[i])
            if (diff < closestDiff) {
                closestIdx = i
                closestDiff = diff
            }
        }
        return closestIdx
    }

    isTrue(value) {
        return [
            1,
            "1",
            true,
            "true",
        ].includes(value)
    }

    isLC() {
        return ["panel_a", "panel_b"].includes(this.element.dataset.panel.toLowerCase().replace(" ", "_"))
    }

    isFia() {
        return !this.isLC()
    }

    getCompoundTypeString(compoundType) {
        const compoundTypeStrings = {
            "analyte": "Analyte",
            "istd": "ISTD",
        }
        return compoundTypeStrings[compoundType]
    }

    updateCounter(event) {
        // this.channel.perform("test_connection")  // invoke a method in the server

        console.log("Updating counter...", event.currentTarget)  // event.currentTarget is the HTML element that triggered the event
        // this.element.textContent += "!"  // update the HTML element on current page without reloading!
        console.log(document.getElementById("ch2"))  // use browser functions like normal  

        let sampleId = this.sampleidTarget.textContent
        console.log(`Invoking get_analyte_sample_data with sample_id ${sampleId}...`)
        this.channel.perform("get_analyte_sample_data", {
            "sampleId": sampleId
        })
    }

    setMultiSamplePageSize(event) {
        const size = event.srcElement.value;
        const pageSize = parseInt(size, 10);

        if (isNaN(pageSize) || pageSize <= 0) {
            console.log("Invalid page size:", size);
            this.MULTI_SAMPLE_PAGE_SIZE = this.MULTI_SAMPLE_DEFAULT_PAGE_SIZE;
        }
        else if (pageSize > this.MULTI_SAMPLE_MAX_PAGE_SIZE) {
            console.log("Page size too large:", size);
            this.MULTI_SAMPLE_PAGE_SIZE = this.MULTI_SAMPLE_MAX_PAGE_SIZE;
        }
        else {
            this.MULTI_SAMPLE_PAGE_SIZE = pageSize;

        }
        console.log("Page size set to:", this.MULTI_SAMPLE_PAGE_SIZE);

    }





}